import { appConfigStore } from 'Models/AppConfigStore';

export const BookingColumnKeys = {
	BOOKING_ID: 'bookingId',
	LEGACY_ID: 'legacyId',
	CREATED_DATE: 'createdDate',
	CREATED_BY: 'createdBy',
	ALTERATION_COUNT: 'alterationCount',
	CUSTOMER: 'customer',
	TYPE: 'type',
	EVENT_NAME: 'eventName',
	EVENT_TICKETS: 'eventTickets',
	EVENT_PRIMARY_CONTACT: 'eventPrimaryContact',
	DIRECTION: 'direction',
	TRIP_DATE: 'tripDate',
	VESSEL: 'vessel',
	VEHICLE_MAKE_MODEL: 'vehicleMakeModel',
	VEHICLE_LENGTH: 'vehicleLength',
	VEHICLE_WEIGHT: 'vehicleWeight',
	VEHICLE_WIDTH: 'vehicleWidth',
	VEHICLE_CHARACTERISTICS: 'vehicleCharacteristics',
	REGO: 'rego',
	DRIVER_CONTACT: 'driverContact',
	TRAILER_TYPE: 'trailerType',
	TRAILER_LENGTH: 'trailerLength',
	TRAILER_WEIGHT: 'trailerWeight',
	TRAILER_WIDTH: 'trailerWidth',
	TRAILER_CHARACTERISTICS: 'trailerCharacteristics',
	PASSENGERS: 'passengers',
	PASSENGER_NAMES: 'passengerNames',
	PASSENGER_CLASSES: 'passengerClasses',
	SEATS: 'seats',
	ADDITIONAL_INFORMATION: 'additionalInformation',
	FREIGHT_DESCRIPTION: 'freightDescription',
	FREIGHT_DIMENSIONS: 'freightDimensions',
	FREIGHT_RECEIVER_DETAILS: 'freightReceiverDetails',
	FREIGHT_NOTIFY_DETAILS: 'freightNotifyDetails',
	ADD_ONS: 'addOns',
	AMOUNT: 'amount',
	STATUS: 'status',
	TRAVEL_STATUS: 'travelStatus',
	CANCELLATION_REASON: 'cancellationReason',
} as const;

// Type-safe key type
export type BookingColumnKey = (typeof BookingColumnKeys)[keyof typeof BookingColumnKeys];

/**
 * The columns a customer's own booking export carries, in the order they appear in the file.
 *
 * Deliberately narrower than what staff get: no legacy id, times-altered, customer name/email/type,
 * travel status or cancellation reason. Cancelled bookings are still identifiable — `Status` reads
 * `Cancelled` for them in its own right.
 *
 * The event columns are the one conditional pair: they are requested whenever events are switched
 * on, and the server drops them again unless the export actually holds an event booking.
 *
 * The config gates mirror the staff column selector's (`getDefaultColumns`) exactly, so a client who
 * has trailers or widths turned off never sees an empty column for them. Keep the two in step: a new
 * column that customers should see needs adding here as well as there.
 *
 * Sending an explicit list also means dynamic booking fields are left out — the server only emits a
 * `dynamicField:<id>` column when that key is in the list — so a customer's answers to custom
 * booking questions do not appear in their file.
 */
export function getCustomerExportColumns(): string[] {
	return [
		BookingColumnKeys.BOOKING_ID,
		BookingColumnKeys.CREATED_DATE,
		BookingColumnKeys.TYPE,
		BookingColumnKeys.DIRECTION,
		// One key, two columns in the file: Trip Date and Trip Time.
		BookingColumnKeys.TRIP_DATE,
		// Asked for whenever events are switched on — we cannot tell from here whether this
		// customer has ever booked one. The server drops these again when nothing in the export
		// turns out to be an event booking, so the columns only reach a customer who has some.
		...(appConfigStore.eventsEnabled
			? [
					BookingColumnKeys.EVENT_NAME,
					BookingColumnKeys.EVENT_TICKETS,
					BookingColumnKeys.EVENT_PRIMARY_CONTACT,
				]
			: []),
		...(appConfigStore.vehicleEnabled
			? [
					BookingColumnKeys.VEHICLE_MAKE_MODEL,
					BookingColumnKeys.VEHICLE_LENGTH,
					BookingColumnKeys.VEHICLE_WEIGHT,
					...(appConfigStore.vehicleWidthEnabled ? [BookingColumnKeys.VEHICLE_WIDTH] : []),
					BookingColumnKeys.VEHICLE_CHARACTERISTICS,
					BookingColumnKeys.REGO,
					BookingColumnKeys.DRIVER_CONTACT,
				]
			: []),
		...(appConfigStore.trailersEnabled
			? [
					BookingColumnKeys.TRAILER_TYPE,
					BookingColumnKeys.TRAILER_LENGTH,
					BookingColumnKeys.TRAILER_WEIGHT,
					...(appConfigStore.vehicleWidthEnabled ? [BookingColumnKeys.TRAILER_WIDTH] : []),
					BookingColumnKeys.TRAILER_CHARACTERISTICS,
				]
			: []),
		// Expands to one column per active passenger type. Ungated, as it is on the staff selector —
		// passengers ride on vehicle bookings too, not only walk-on ones.
		BookingColumnKeys.PASSENGERS,
		...(appConfigStore.passengerInfoEnabled ? [BookingColumnKeys.PASSENGER_NAMES] : []),
		// Only where the product sells by fare type. Two permanently empty columns is a worse
		// answer than none, and a site with the setting off has never seen either.
		...(appConfigStore.passengerClassesEnabled
			? [BookingColumnKeys.PASSENGER_CLASSES, BookingColumnKeys.SEATS]
			: []),
		...(appConfigStore.freightEnabled
			? [
					BookingColumnKeys.FREIGHT_DESCRIPTION,
					BookingColumnKeys.FREIGHT_DIMENSIONS,
					...(appConfigStore.freightReceiverDetailsEnabled
						? [BookingColumnKeys.FREIGHT_RECEIVER_DETAILS]
						: []),
					...(appConfigStore.freightNotifyDetailsEnabled ? [BookingColumnKeys.FREIGHT_NOTIFY_DETAILS] : []),
				]
			: []),
		...(appConfigStore.vehicleNoteEnabled || appConfigStore.passengerNoteEnabled
			? [BookingColumnKeys.ADDITIONAL_INFORMATION]
			: []),
		BookingColumnKeys.ADD_ONS,
		// One key, two columns in the file: Paid Amount and Total Amount.
		BookingColumnKeys.AMOUNT,
		BookingColumnKeys.STATUS,
	];
}
